import { List, Record } from 'immutable'
import { Address, IAddress } from './Address'
import { Area, IArea } from './Area'
import { AssociatedContact, IAssociatedContact } from './AssociatedContact'
import { AssociatedListing, IAssociatedListing } from './AssociatedListing'
import { Attachment, IAttachment } from './Attachment'
import { Comp, IComp } from './Comp'
import { BuildingClass, Corridor, ImageSize, PropertyType } from './enums'
import { FeedItem, IFeedItem } from './FeedItem'
import { GeoJsonPoint, IGeoJsonPoint } from './GeoJsonPoint'
import { ILot, Lot } from './Lot'
import {
  IPropertyFinancialDetails,
  PropertyFinancialDetails,
} from './PropertyFinancialDetails'
import { ISign, Sign } from './Sign'
import { ISocialConnection, SocialConnection } from './SocialConnection'
import { ISpace, Space } from './Space'
import { ISuccessStory, SuccessStory } from './SuccessStory'
import { ITransactionHistory, TransactionHistory } from './TransactionHistory'

export interface IProperty {
  readonly address: IAddress
  readonly assignmentCount: number
  readonly associated: List<IAssociatedContact>
  readonly attachments: List<IAttachment>
  readonly avgDailyRate: number
  readonly avgOccupancy: number
  readonly buildingClass: BuildingClass
  readonly ceilingHeight: number
  readonly comps: List<IComp>
  readonly corridor: Corridor
  readonly creator: string
  readonly financialData: IPropertyFinancialDetails
  readonly gps: IGeoJsonPoint
  readonly grossLeaseableArea: number
  readonly grossRentMultiplier: number
  readonly hasStarred: List<string>
  readonly highlights: List<string>
  readonly id: string
  readonly leads: List<IFeedItem>
  readonly listings: List<IAssociatedListing>
  readonly locationDescription: string
  readonly lotDetails: List<ILot>
  readonly name: string
  readonly noOfDockHighDoorsLoading: number
  readonly noOfDriveInGradeLevelDoors: number
  readonly noOfUnits: number
  readonly occupancy: number
  readonly owners: List<IAssociatedContact>
  readonly parcelId: string
  readonly photos: List<IAttachment>
  readonly propertyDescription: string
  readonly propertyTypes: List<PropertyType>
  readonly revPerPAR: number
  readonly quickAdd: boolean
  readonly signs: List<ISign>
  readonly siteId?: string
  readonly social: List<ISocialConnection>
  readonly spaceDetails: List<ISpace>
  readonly successStories: List<ISuccessStory>
  readonly thumbnailImage?: IAttachment
  readonly thumbnailImageId: string
  readonly totalBuildingSize: number
  readonly totalLotSize: IArea
  readonly transactionHistory: List<ITransactionHistory>
  readonly yearBuilt: number
  readonly zoningDescription: string
  readonly chatChannelId: string

  getThumbnailOrDefault(size: ImageSize): string
}

const PropertyRecord = Record({
  address: new Address(),
  assignmentCount: 0,
  associated: List<AssociatedContact>(),
  attachments: List<Attachment>(),
  avgDailyRate: 0,
  avgOccupancy: 0,
  buildingClass: BuildingClass.NotApplicable,
  ceilingHeight: 0,
  comps: List<Comp>(),
  corridor: Corridor.NotApplicable,
  creator: '',
  financialData: new PropertyFinancialDetails(),
  gps: new GeoJsonPoint(),
  grossLeaseableArea: 0,
  grossRentMultiplier: 0,
  hasStarred: List<string>(),
  highlights: List<string>(),
  id: '',
  leads: List<FeedItem>(),
  listings: List<AssociatedListing>(),
  locationDescription: '',
  lotDetails: List<Lot>(),
  name: '',
  noOfDockHighDoorsLoading: 0,
  noOfDriveInGradeLevelDoors: 0,
  noOfUnits: 0,
  occupancy: 0,
  owners: List<AssociatedContact>(),
  parcelId: '',
  photos: List<Attachment>(),
  propertyDescription: '',
  propertyTypes: List<PropertyType>(),
  quickAdd: false,
  revPerPAR: 0,
  signs: List<Sign>(),
  siteId: undefined,
  social: List<SocialConnection>(),
  spaceDetails: List<Space>(),
  successStories: List<SuccessStory>(),
  thumbnailImage: undefined,
  thumbnailImageId: '',
  totalBuildingSize: 0,
  totalLotSize: new Area(),
  transactionHistory: List<TransactionHistory>(),
  yearBuilt: 0,
  zoningDescription: '',
  chatChannelId: '',
})

export class Property extends PropertyRecord implements IProperty {
  public static fromJS(initialState?: any) {
    if (!initialState) {
      return new Property()
    }

    const {
      address,
      associated,
      attachments,
      comps,
      financialData,
      gps,
      hasStarred,
      highlights,
      leads,
      listings,
      lotDetails,
      owners,
      photos,
      propertyTypes,
      signs,
      social,
      spaceDetails,
      successStories,
      thumbnailImage,
      totalLotSize,
      transactionHistory,
      ...rest
    } = initialState

    const property = new Property({
      address: Address.fromJS(address),
      associated: Array.isArray(associated)
        ? List(associated.map((a) => AssociatedContact.fromJS(a)))
        : List<AssociatedContact>(),
      attachments: Array.isArray(attachments)
        ? List(attachments.map((a) => Attachment.fromJS(a)))
        : List<Attachment>(),
      comps: Array.isArray(comps)
        ? List(comps.map((c) => Comp.fromJS(c)))
        : List<Comp>(),
      financialData: PropertyFinancialDetails.fromJS(financialData),
      gps: GeoJsonPoint.fromJS(gps),
      hasStarred: List(hasStarred || []),
      highlights: List(highlights || []),
      leads: Array.isArray(leads)
        ? List(leads.map((l) => FeedItem.fromJS(l)))
        : List<FeedItem>(),
      listings: Array.isArray(listings)
        ? List(listings.map((l) => AssociatedListing.fromJS(l)))
        : List<AssociatedListing>(),
      lotDetails: Array.isArray(lotDetails)
        ? List(lotDetails.map((l) => Lot.fromJS(l)))
        : List<Lot>(),
      owners: Array.isArray(owners)
        ? List(owners.map((o) => AssociatedContact.fromJS(o)))
        : List<AssociatedContact>(),
      photos: Array.isArray(photos)
        ? List(photos.map((p) => Attachment.fromJS(p)))
        : List<Attachment>(),
      propertyTypes: Array.isArray(propertyTypes)
        ? List(propertyTypes)
        : List<PropertyType>(),
      signs: Array.isArray(signs)
        ? List(signs.map((s) => Sign.fromJS(s)))
        : List<Sign>(),
      social: Array.isArray(social)
        ? List(social.map((s) => SocialConnection.fromJS(s)))
        : List<SocialConnection>(),
      spaceDetails: Array.isArray(spaceDetails)
        ? List(spaceDetails.map((s) => Space.fromJS(s)))
        : List<Space>(),
      successStories: Array.isArray(successStories)
        ? List(successStories.map((s) => SuccessStory.fromJS(s)))
        : List<SuccessStory>(),
      thumbnailImage: thumbnailImage
        ? Attachment.fromJS(thumbnailImage)
        : undefined,
      totalLotSize: Area.fromJS(totalLotSize),
      transactionHistory: Array.isArray(transactionHistory)
        ? List(transactionHistory.map((t) => TransactionHistory.fromJS(t)))
        : List<TransactionHistory>(),
      ...rest,
    })

    return property
  }

  public readonly address: Address
  public readonly assignmentCount: number
  public readonly associated: List<AssociatedContact>
  public readonly attachments: List<Attachment>
  public readonly avgDailyRate: number
  public readonly avgOccupancy: number
  public readonly buildingClass: BuildingClass
  public readonly ceilingHeight: number
  public readonly comps: List<Comp>
  public readonly corridor: Corridor
  public readonly creator: string
  public readonly financialData: PropertyFinancialDetails
  public readonly gps: GeoJsonPoint
  public readonly grossLeaseableArea: number
  public readonly grossRentMultiplier: number
  public readonly hasStarred: List<string>
  public readonly highlights: List<string>
  public readonly id: string
  public readonly leads: List<FeedItem>
  public readonly listings: List<AssociatedListing>
  public readonly locationDescription: string
  public readonly lotDetails: List<Lot>
  public readonly name: string
  public readonly noOfDockHighDoorsLoading: number
  public readonly noOfDriveInGradeLevelDoors: number
  public readonly noOfUnits: number
  public readonly occupancy: number
  public readonly owners: List<AssociatedContact>
  public readonly parcelId: string
  public readonly photos: List<Attachment>
  public readonly propertyDescription: string
  public readonly propertyTypes: List<PropertyType>
  public readonly quickAdd: boolean
  public readonly revPerPAR: number
  public readonly signs: List<Sign>
  public readonly siteId?: string
  public readonly social: List<SocialConnection>
  public readonly spaceDetails: List<Space>
  public readonly successStories: List<SuccessStory>
  public readonly thumbnailImage?: Attachment
  public readonly thumbnailImageId: string
  public readonly totalBuildingSize: number
  public readonly totalLotSize: Area
  public readonly transactionHistory: List<TransactionHistory>
  public readonly yearBuilt: number
  public readonly zoningDescription: string
  public readonly chatChannelId: string

  constructor(props?: Partial<Property>) {
    props ? super(props) : super()
  }

  public getThumbnailOrDefault(size: ImageSize): string {
    if (this.thumbnailImage) {
      return this.thumbnailImage.getImageUrl(size)
    } else {
      return `/images/default-property.png`
    }
  }
}
